// 5-route grid editor for outbound rules — parent/child model.
// Per-route gateway picker with strip/prepend digit transforms.

import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronDown, Trash2, Plus, Save, Search, ArrowLeft, Wand2, X } from 'lucide-react';
import { api } from '../lib/api';

const POSITIONS = [1, 2, 3, 4, 5];

type RouteSlot = {
  position: number;
  gateway_uuid: string | null;
  strip_digits?: number;
  prepend_digits?: string;
  append_digits?: string;
  override_caller_id?: string;
};

type RuleForm = {
  rule_uuid?: string;
  domain_uuid: string;
  name: string;
  prefix: string;
  length_filter: string;
  extension_filter: string;
  department_uuids: string[];
  priority: number;
  enabled: boolean;
  routes: RouteSlot[];
};

function emptyRule(domain_uuid: string): RuleForm {
  return {
    domain_uuid,
    name: '',
    prefix: '',
    length_filter: '',
    extension_filter: '',
    department_uuids: [],
    priority: 100,
    enabled: true,
    routes: POSITIONS.map(p => ({ position: p, gateway_uuid: null })),
  };
}

export default function OutboundRulesV2() {
  const me = api.me();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RuleForm | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  const { data: rules = [] } = useQuery({
    queryKey: ['outbound-v2', me?.domain_uuid],
    queryFn: () => me?.domain_uuid ? api.outboundRulesV2.list(me.domain_uuid) : Promise.resolve([]),
    enabled: !!me?.domain_uuid,
  });

  const { data: trunksRaw = [] } = useQuery({
    queryKey: ['trunks'],
    queryFn: () => api.trunks.list(),
  });
  // /api/trunks returns trunks + livechat + whatsapp + facebook channels.
  // The 5-route grid only routes voice — filter to actual SIP gateways.
  const trunks = (trunksRaw as any[]).filter(t => t.itemType === 'trunk' || t.gateway_uuid);

  const move = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      api.outboundRulesV2.move(id, direction),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbound-v2', me?.domain_uuid] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.outboundRulesV2.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbound-v2', me?.domain_uuid] }),
  });

  const save = useMutation({
    mutationFn: async (form: RuleForm) => {
      const cleanRoutes = form.routes.filter(r => r.gateway_uuid);
      if (form.rule_uuid) return api.outboundRulesV2.update(form.rule_uuid, { ...form, routes: cleanRoutes });
      return api.outboundRulesV2.create({ ...form, routes: cleanRoutes });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbound-v2', me?.domain_uuid] });
      setEditing(null);
    },
  });

  async function openEdit(ruleUuid: string) {
    const rule: any = await api.outboundRulesV2.get(ruleUuid);
    setEditing({
      rule_uuid: rule.rule_uuid,
      domain_uuid: rule.domain_uuid,
      name: rule.name || '',
      prefix: rule.prefix || '',
      length_filter: rule.length_filter || '',
      extension_filter: rule.extension_filter || '',
      department_uuids: rule.department_uuids || [],
      priority: rule.priority || 100,
      enabled: rule.enabled !== false,
      routes: rule.routes || POSITIONS.map(p => ({ position: p, gateway_uuid: null })),
    });
  }

  async function runTest() {
    if (!me?.domain_uuid || !testNumber) return;
    try {
      const r = await api.outboundRulesV2.testRoute({ number: testNumber, domain_uuid: me.domain_uuid });
      setTestResult(r);
    } catch (e: any) {
      setTestResult({ error: e.message });
    }
  }

  if (editing) return <EditForm form={editing} setForm={setEditing} trunks={trunks} onSave={save.mutate} saving={save.isPending} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Outbound Rules</h1>
        <div className="flex items-center gap-2">
          <button className="btn" onClick={() => setWizardOpen(true)}>
            <Wand2 size={16} className="inline mr-1" /> Auto-create rules
          </button>
          <button className="btn-primary" onClick={() => me?.domain_uuid && setEditing(emptyRule(me.domain_uuid))}>
            <Plus size={16} className="inline mr-1" /> Add Rule
          </button>
        </div>
      </div>

      {wizardOpen && (
        <WizardModal
          defaultDomain={me?.domain_uuid || ''}
          trunks={trunks}
          onClose={() => setWizardOpen(false)}
          onCreated={() => { qc.invalidateQueries({ queryKey: ['outbound-v2', me?.domain_uuid] }); setWizardOpen(false); }}
        />
      )}

      <div className="card p-3 flex items-center gap-2">
        <Search size={16} className="text-textSecondary" />
        <input
          className="input flex-1"
          placeholder="Test a number against current rules…"
          value={testNumber}
          onChange={e => setTestNumber(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && runTest()}
        />
        <button className="btn" onClick={runTest}>Test Route</button>
      </div>
      {testResult && (
        <div className="card p-3 font-mono text-sm whitespace-pre-wrap">
          {JSON.stringify(testResult, null, 2)}
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Prefix</th>
              <th>Length</th>
              <th>From Ext</th>
              <th>Priority</th>
              <th>Routes</th>
              <th>Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r: any, i: number) => (
              <tr key={r.rule_uuid} className="hover:bg-elevated cursor-pointer" onClick={() => openEdit(r.rule_uuid)}>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-ghost p-1" disabled={i === 0} onClick={() => move.mutate({ id: r.rule_uuid, direction: 'up' })}><ChevronUp size={14} /></button>
                  <button className="btn-ghost p-1" disabled={i === rules.length - 1} onClick={() => move.mutate({ id: r.rule_uuid, direction: 'down' })}><ChevronDown size={14} /></button>
                </td>
                <td className="font-medium">{r.name}</td>
                <td className="font-mono">{r.prefix || '—'}</td>
                <td className="font-mono">{r.length_filter || '—'}</td>
                <td className="font-mono">{r.extension_filter || '—'}</td>
                <td>{r.priority}</td>
                <td><span className="badge">{r.route_count} / 5</span></td>
                <td>{r.enabled ? <span className="badge bg-success/20 text-success">on</span> : <span className="badge bg-textSecondary/20">off</span>}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete rule?') && remove.mutate(r.rule_uuid)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-textSecondary">No outbound rules yet — calls can't reach the PSTN until a rule is created.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Auto-create wizard: dept + area code + caller ID + trunk → 7/10/11 + 911 ──
function normalizeCallerId(input: string): string {
  const s = (input || '').trim();
  if (!s) return '';
  if (s.startsWith('+')) { const d = s.slice(1).replace(/\D/g, ''); return d ? '+' + d : ''; }
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  return digits ? '+' + digits : '';
}

function WizardModal({
  defaultDomain, trunks, onClose, onCreated,
}: {
  defaultDomain: string;
  trunks: any[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => api.tenants.list() });
  const [domainUuid, setDomainUuid] = useState(defaultDomain);
  const [areaCode, setAreaCode]     = useState('');
  const [callerId, setCallerId]     = useState('');
  const [gatewayUuid, setGatewayUuid] = useState('');
  const [include911, setInclude911] = useState(true);
  const [err, setErr] = useState('');

  const ac = areaCode.replace(/\D/g, '');
  const cid = normalizeCallerId(callerId);
  const acOk = ac.length === 3;

  // Live preview — mirrors api/lib/outbound_wizard.buildStandardRules exactly.
  const preview = useMemo(() => {
    if (!acOk) return [];
    const rows: { name: string; match: string; result: string }[] = [];
    if (include911) rows.push({ name: '911 Emergency', match: 'dials 911', result: '911 (as-is)' });
    rows.push({ name: `Local 7-digit (${ac})`, match: '7-digit', result: `+1${ac} + number` });
    rows.push({ name: '10-digit (NANP)',        match: '10-digit', result: '+1 + number' });
    rows.push({ name: '11-digit (1 + number)',  match: '11-digit (starts 1)', result: '+ number' });
    return rows;
  }, [acOk, ac, include911]);

  const create = useMutation({
    mutationFn: () => api.outboundRulesV2.wizard({
      domain_uuid: domainUuid, gateway_uuid: gatewayUuid,
      area_code: ac, caller_id: cid, include_911: include911,
    }),
    onSuccess: onCreated,
    onError: (e: any) => setErr(String(e.message || e)),
  });

  function submit() {
    setErr('');
    if (!domainUuid) return setErr('Pick a department.');
    if (!acOk) return setErr('Area code must be 3 digits.');
    if (!cid) return setErr('Enter a valid primary caller ID (10 or 11 digits).');
    if (!gatewayUuid) return setErr('Pick a trunk.');
    create.mutate();
  }

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4"
         onMouseDown={onClose}>
      <div className="card w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-auto"
           onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Wand2 size={18} className="text-primary" />
          <h2 className="text-lg font-semibold flex-1">Auto-create outbound rules</h2>
          <button className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="text-sm text-textSecondary">
          Creates the standard 7-digit, 10-digit and 11-digit rules (all normalized to E.164)
          plus a 911 rule — each routed on the trunk you pick, presenting your primary number.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-sm font-medium">Department</label>
            <select className="input mt-1" value={domainUuid} onChange={e => setDomainUuid(e.target.value)}>
              <option value="">Select department…</option>
              {(tenants.data || []).map((t: any) => (
                <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Area code</label>
            <input className="input mt-1 font-mono" value={areaCode} maxLength={5}
                   onChange={e => setAreaCode(e.target.value)} placeholder="989" />
          </div>
          <div>
            <label className="text-sm font-medium">Primary caller ID</label>
            <input className="input mt-1 font-mono" value={callerId}
                   onChange={e => setCallerId(e.target.value)} placeholder="(989) 555-1212" />
            {callerId && (
              <div className="text-xs text-textSecondary mt-1">
                Presents as <span className="font-mono">{cid || '— invalid —'}</span>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium">Trunk</label>
            <select className="input mt-1" value={gatewayUuid} onChange={e => setGatewayUuid(e.target.value)}>
              <option value="">Select trunk…</option>
              {trunks.map((t: any) => (
                <option key={t.gateway_uuid || t.id} value={t.gateway_uuid || t.id}>{t.gateway_name || t.description || t.name || t.gateway}{(t.gateway_name || t.description) && t.gateway ? ` (${t.gateway})` : ''}</option>
              ))}
            </select>
          </div>
          <label className="col-span-2 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={include911} onChange={e => setInclude911(e.target.checked)} />
            Also add a 911 emergency rule (recommended)
          </label>
        </div>

        {preview.length > 0 && (
          <div className="card p-0 overflow-hidden bg-elevated/40">
            <table className="table text-sm">
              <thead><tr><th>Rule</th><th>Matches</th><th>Sends to trunk</th></tr></thead>
              <tbody>
                {preview.map(r => (
                  <tr key={r.name}>
                    <td className="font-medium">{r.name}</td>
                    <td className="font-mono">{r.match}</td>
                    <td className="font-mono">{r.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {err && <div className="text-sm text-danger">{err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : `Create ${preview.length || ''} rule${preview.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body);
}

function EditForm({
  form, setForm, trunks, onSave, saving, onCancel,
}: {
  form: RuleForm;
  setForm: (f: RuleForm) => void;
  trunks: any[];
  onSave: (f: RuleForm) => void;
  saving: boolean;
  onCancel: () => void;
}) {
  function updateRoute(pos: number, patch: Partial<RouteSlot>) {
    setForm({
      ...form,
      routes: form.routes.map(r => r.position === pos ? { ...r, ...patch } : r),
    });
  }

  // Tenant/department list for the "Calls from Departments" multi-select.
  const tenants = useQuery<any[]>({
    queryKey: ['tenants'],
    queryFn: () => api.tenants.list(),
  });

  function toggleDept(uuid: string) {
    const next = new Set(form.department_uuids || []);
    if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
    setForm({ ...form, department_uuids: Array.from(next) });
  }

  return (
    <div className="space-y-3">
      {/* Header — back arrow + title + Save (matches 3CX shape) */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /></button>
        <h1 className="text-xl font-semibold flex-1">{form.rule_uuid ? 'Edit Outbound Rule' : 'Add Outbound Rule'}</h1>
        <button className="btn-primary" disabled={saving || !form.name} onClick={() => onSave(form)}>
          <Save size={14} className="inline mr-1" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Top section — Rule Name + 3 match-criteria fields */}
      <div className="card p-4 space-y-4">
        <div>
          <label className="text-sm font-medium">Rule Name <span className="text-danger">*</span></label>
          <input className="input mt-1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">Calls to numbers starting with prefix</label>
            <input className="input mt-1 font-mono" value={form.prefix} onChange={e => setForm({ ...form, prefix: e.target.value })} />
            <div className="text-xs text-textSecondary mt-1">e.g. 0-3,5,6. Leave empty for any prefix</div>
          </div>
          <div>
            <label className="text-sm font-medium">Calls from extension(s)</label>
            <input className="input mt-1 font-mono" value={form.extension_filter} onChange={e => setForm({ ...form, extension_filter: e.target.value })} />
            <div className="text-xs text-textSecondary mt-1">e.g. 1000,1003-1005. Leave empty for any extension</div>
          </div>
          <div>
            <label className="text-sm font-medium">Calls to numbers with a length of</label>
            <input className="input mt-1 font-mono" value={form.length_filter} onChange={e => setForm({ ...form, length_filter: e.target.value })} />
            <div className="text-xs text-textSecondary mt-1">e.g. 9,10,13-15. Leave empty for any length</div>
          </div>
        </div>

        {/* Calls from Departments — multi-select */}
        <div>
          <h3 className="font-semibold pt-2">Calls from Departments</h3>
          <p className="text-sm text-textSecondary mt-1">Select which Department(s) can use this outbound rule</p>
          <DeptMultiSelect
            tenants={tenants.data || []}
            selected={form.department_uuids || []}
            onToggle={toggleDept}
          />
        </div>
      </div>

      {/* Make outbound calls on — 5-route table */}
      <div className="card p-4 space-y-3">
        <h2 className="font-semibold">Make outbound calls on</h2>
        <p className="text-sm text-textSecondary">
          Configure a route and specify 4 backup routes in case the main fails. Each route can be configured differently. For the rule to work, at least one route should be configured. Failure to do so will result in the call being dropped.
        </p>

        <table className="table">
          <thead>
            <tr>
              <th className="w-24"></th>
              <th>Trunk</th>
              <th className="w-32">Strip digits</th>
              <th className="w-40">Prepend</th>
              <th className="w-48">Outbound caller ID</th>
            </tr>
          </thead>
          <tbody>
            {form.routes.map(rt => (
              <RouteRow key={rt.position} route={rt} trunks={trunks} onChange={patch => updateRoute(rt.position, patch)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RouteRow({ route, trunks, onChange }: { route: RouteSlot; trunks: any[]; onChange: (p: Partial<RouteSlot>) => void }) {
  const used = !!route.gateway_uuid;
  return (
    <tr>
      <td className="font-medium text-textSecondary">Route {route.position}</td>
      <td>
        <select className="input" value={route.gateway_uuid || ''} onChange={e => onChange({ gateway_uuid: e.target.value || null })}>
          <option value="">Select Trunk</option>
          {trunks.map((t: any) => (
            <option key={t.gateway_uuid || t.id} value={t.gateway_uuid || t.id}>{t.gateway_name || t.description || t.name || t.gateway}{(t.gateway_name || t.description) && t.gateway ? ` (${t.gateway})` : ''}</option>
          ))}
        </select>
      </td>
      <td>
        <input className="input font-mono" type="number" min="0" disabled={!used}
               value={route.strip_digits ?? ''}
               onChange={e => onChange({ strip_digits: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })} />
      </td>
      <td>
        <input className="input font-mono" disabled={!used}
               value={route.prepend_digits || ''}
               onChange={e => onChange({ prepend_digits: e.target.value })} />
      </td>
      <td>
        <input className="input font-mono" disabled={!used}
               value={route.override_caller_id || ''}
               onChange={e => onChange({ override_caller_id: e.target.value })} />
      </td>
    </tr>
  );
}

function DeptMultiSelect({ tenants, selected, onToggle }: { tenants: any[]; selected: string[]; onToggle: (uuid: string) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const selSet = new Set(selected);
  const labels = tenants.filter(t => selSet.has(t.domain_uuid)).map(t => t.domain_description || t.domain_name);

  // The panel is rendered in a PORTAL at document.body with fixed positioning. The .card class uses
  // backdrop-filter, which creates a stacking context — an absolutely-positioned dropdown (any z-index)
  // stays trapped inside this card and is painted over by the next card. A portal escapes all of that.
  const place = () => { const r = btnRef.current?.getBoundingClientRect(); if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width }); };
  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e: MouseEvent) => { if (btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false); };
    const onReposition = () => place();
    const t = setTimeout(() => document.addEventListener('mousedown', onDoc), 0);  // don't catch the opening click
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDoc); window.removeEventListener('scroll', onReposition, true); window.removeEventListener('resize', onReposition); };
  }, [open]);

  return (
    <div className="relative mt-2">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input text-left flex items-center justify-between w-full"
      >
        <span className={labels.length ? '' : 'text-textSecondary'}>
          {labels.length ? labels.join(', ') : 'Select departments...'}
        </span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && rect && createPortal(
        <div
          className="card max-h-60 overflow-auto p-1 shadow-xl"
          style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 1000 }}
          onMouseDown={e => e.stopPropagation()}  // clicks inside the panel don't trigger the outside-close
        >
          {tenants.length === 0 && <div className="px-3 py-2 text-sm text-textSecondary">No departments yet.</div>}
          {tenants.map((t: any) => (
            <label key={t.domain_uuid} className="flex items-center gap-2 px-2 py-1.5 hover:bg-elevated rounded cursor-pointer text-sm">
              <input type="checkbox" checked={selSet.has(t.domain_uuid)} onChange={() => onToggle(t.domain_uuid)} />
              {t.domain_description || t.domain_name}
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-textSecondary">{hint}</div>}
    </div>
  );
}
